import React from 'react'
import styled from '@emotion/styled'
import GrooverText from './GrooverText'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Language } from '../../../pure-js/types/Antiloop'

interface PrivateLessonsStepHeaderProps {
  currentStep: 1 | 2 | 3 | 4
  onStepClick?: (step: 1 | 2 | 3 | 4) => void
  language?: Language
  compact?: boolean
}

const PrivateLessonsStepHeader: React.FC<PrivateLessonsStepHeaderProps> = ({
  currentStep,
  onStepClick,
  language,
  compact
}) => {
  const steps = [
    { internalStep: 1 as 1 | 2 | 3 | 4, displayNumber: 1, textKey: 'privateLessonsCheckoutStepPackage' },
    { internalStep: 3 as 1 | 2 | 3 | 4, displayNumber: 2, textKey: 'privateLessonsCheckoutStepBooking' },
    { internalStep: 4 as 1 | 2 | 3 | 4, displayNumber: 3, textKey: 'privateLessonsCheckoutPayment' }
  ]

  const getStepStatus = (internalStepNumber: number) => {
    if (currentStep > internalStepNumber) return 'completed'
    if (currentStep === internalStepNumber) return 'current'
    return 'upcoming'
  }

  return (
    <HeaderContainer $compact={compact}>
      <StepsContainer $compact={compact}>
        {steps.map((step, index) => {
          const status = getStepStatus(step.internalStep)
          const isClickable = status === 'completed' && onStepClick

          return (
            <React.Fragment key={step.internalStep}>
              <StepWrapper
                onClick={isClickable ? () => onStepClick?.(step.internalStep) : () => {}}
                $isClickable={!!isClickable}
              >
                <StepCircle $status={status} $compact={compact}>
                  {status === 'completed' ? (
                    <StyledCheckIcon>
                      <CheckCircleIcon />
                    </StyledCheckIcon>
                  ) : (
                    <StepNumber $status={status} $compact={compact}>
                      {step.displayNumber}
                    </StepNumber>
                  )}
                </StepCircle>
                <StepLabel $status={status} $compact={compact}>
                  <GrooverText textKey={step.textKey} language={language} />
                </StepLabel>
              </StepWrapper>
              {index < steps.length - 1 && <StepDivider $compact={compact} />}
            </React.Fragment>
          )
        })}
      </StepsContainer>
    </HeaderContainer>
  )
}

const HeaderContainer = styled.div<{ $compact?: boolean }>`
  background-color: white;
  color: #000;
  padding: ${({ $compact }) => ($compact ? '12px 14px' : '20px')};
  width: 100%;
  border-bottom: 1px solid #e0e0e0;

  @media (max-width: 400px) {
    padding: 10px 8px;
  }
`

const StepsContainer = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $compact }) => ($compact ? '10px' : '16px')};
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 400px) {
    gap: 4px;
  }
`

const StepWrapper = styled.div<{ $isClickable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: ${({ $isClickable }) => ($isClickable ? 0.8 : 1)};
  }
`

const StepCircle = styled.div<{ $status: 'completed' | 'current' | 'upcoming'; $compact?: boolean }>`
  width: ${({ $compact }) => ($compact ? '32px' : '40px')};
  height: ${({ $compact }) => ($compact ? '32px' : '40px')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $status }) =>
    $status === 'completed' ? '#7c3aed' : $status === 'current' ? '#7c3aed' : '#e0e0e0'};
  border: 2px solid ${({ $status }) => ($status === 'current' ? '#7c3aed' : 'transparent')};
  transition: all 0.2s ease;

  @media (max-width: 400px) {
    width: 28px;
    height: 28px;
  }
`

const StepNumber = styled.span<{ $status?: 'completed' | 'current' | 'upcoming'; $compact?: boolean }>`
  color: ${({ $status }) => ($status === 'upcoming' ? '#666' : 'white')};
  font-weight: 600;
  font-size: ${({ $compact }) => ($compact ? '14px' : '16px')};

  @media (max-width: 400px) {
    font-size: 12px;
  }
`

const StepLabel = styled.span<{ $status: 'completed' | 'current' | 'upcoming'; $compact?: boolean }>`
  color: ${({ $status }) => ($status === 'upcoming' ? '#999' : '#000')};
  font-size: ${({ $compact }) => ($compact ? '12px' : '14px')};
  font-weight: ${({ $status }) => ($status === 'current' ? 600 : 400)};
  text-align: center;
  white-space: nowrap;
  font-family: 'Plus Jakarta Sans', sans-serif;

  @media (max-width: 480px) {
    display: none;
  }
`

const StepDivider = styled.div<{ $compact?: boolean }>`
  width: ${({ $compact }) => ($compact ? '20px' : '40px')};
  /* Spacer only (no visible line) */
  height: 0px;
  background-color: transparent;
  margin: ${({ $compact }) => ($compact ? '0 4px' : '0 8px')};

  @media (max-width: 400px) {
    width: 12px;
    margin: 0 2px;
  }
`

const StyledCheckIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 20px;
    color: white;
    fill: white;
  }
`

export default PrivateLessonsStepHeader
